'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';

export default function HeroSection({ lang = 'it' }: {lang?: 'it' | 'en';}) {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 10;
      const floats = heroRef.current.querySelectorAll<HTMLElement>('[data-parallax]');
      floats.forEach((el) => {
        const speed = parseFloat(el.dataset.parallax || '1');
        el.style.transform = `translate(${x * speed}px, ${y * speed}px)`;
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      style={{
        position: 'relative',
        minHeight: '100svh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: '#0B1F2E'
      }}>
      
      {/* Background video layer */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>

        {/* Vimeo background video */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 1 }}>
          <iframe
            src="https://player.vimeo.com/video/1173470797?background=1&autoplay=1&loop=1&muted=1&autopause=0"
            allow="autoplay; fullscreen"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '177.78vh',
              minWidth: '100%',
              height: '56.25vw',
              minHeight: '100%',
              border: 'none',
              pointerEvents: 'none'
            }}
            title="E-Foil hero background video"
          />
        </div>

        {/* Dark cinematic gradient overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            background: 'linear-gradient(to bottom, rgba(5,15,25,0.75) 0%, rgba(5,15,25,0.45) 40%, rgba(5,15,25,0.55) 70%, rgba(5,15,25,0.85) 100%)'
          }} />
        
      </div>

      {/* Animated water particles */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden', pointerEvents: 'none' }}>
        {[...Array(6)].map((_, i) =>
        <div
          key={i}
          style={{
            position: 'absolute',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,212,255,0.15) 0%, transparent 70%)',
            width: `${150 + i * 60}px`,
            height: `${150 + i * 60}px`,
            top: `${10 + i * 15}%`,
            left: `${5 + i * 14}%`,
            animation: `float ${4 + i * 0.8}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`
          }} />
        )}
      </div>

      {/* Content */}
      <div className="container-main" style={{ position: 'relative', zIndex: 2, paddingTop: '60px', paddingBottom: '120px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr)',
            gap: '40px',
            alignItems: 'center'
          }}>
          
          {/* Left: headline + CTA */}
          <div style={{ maxWidth: '680px' }}>
            <div className="badge badge-cyan" style={{ marginBottom: '20px' }}>
              {lang === 'it' ? 'Lago Maggiore & Lago d\'Orta · Esperienza Guidata' : 'Lake Maggiore & Lake Orta · 2 Active Locations'}
            </div>

            <h1 className="hero-headline" style={{ marginBottom: '20px' }}>
              {lang === 'it' ?
              <>
                  Vola<br />
                  <span style={{ color: '#00D4FF', fontStyle: 'italic' }}>sull&apos;acqua.</span>
                </> :

              <>
                  Fly<br />
                  <span style={{ color: '#00D4FF', fontStyle: 'italic' }}>above water.</span>
                </>
              }
            </h1>

            <p className="hero-sub" style={{ marginBottom: '36px', maxWidth: '480px' }}>
              {lang === 'it'? <>Prova l&apos;e-foil tra Lago Maggiore e Lago d&apos;Orta.<br />In 15 minuti impari le basi e inizi a volare. Attrezzatura e istruttore inclusi.</> : 'Rent an e-foil on Lake Maggiore. No experience needed. 15-min briefing included, all equipment provided.'}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '40px' }}>
              <Link href="/booking" className="btn-primary" style={{ fontSize: '15px', padding: '16px 32px' }}>
                <Icon name="CalendarDaysIcon" size={18} variant="solid" />
                {lang === 'it' ? 'Prenota ora' : 'Book now'}
              </Link>
              <Link href="/experiences-pricing" className="btn-secondary" style={{ fontSize: '15px', padding: '15px 24px' }}>
                {lang === 'it' ? 'Scopri i pacchetti' : 'View packages'}
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
            </div>

            {/* Trust badges */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {[
              { icon: 'ShieldCheckIcon', text: lang === 'it' ? 'Attrezzatura inclusa' : 'Equipment included' },
              { icon: 'AcademicCapIcon', text: lang === 'it' ? 'Briefing 15 min' : '15-min briefing' },
              { icon: 'StarIcon', text: '5 ★' }].
              map((badge) =>
              <div
                key={badge.text}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  color: 'rgba(240,247,255,0.65)',
                  fontWeight: 500
                }}>
                
                  <Icon name={badge.icon as 'ShieldCheckIcon'} size={14} style={{ color: '#00D4FF' }} />
                  {badge.text}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating booking card */}
      <div
        data-parallax="0.5"
        style={{
          position: 'absolute',
          right: 'clamp(16px, 5vw, 80px)',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 3,
          width: 'min(320px, calc(100vw - 32px))',
          display: 'none'
        }}
        className="lg:block">
        
        <div
          className="glass"
          style={{
            borderRadius: '24px',
            padding: '24px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,212,255,0.2)',
            animation: 'float 5s ease-in-out infinite'
          }}>
          
          <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.4)', marginBottom: '16px' }}>
            {lang === 'it' ? 'Prossimi slot disponibili' : 'Next available slots'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            {[
            { day: lang === 'it' ? 'Oggi' : 'Today', time: '15:00', avail: true },
            { day: lang === 'it' ? 'Domani' : 'Tomorrow', time: '10:00', avail: true },
            { day: lang === 'it' ? 'Dom 8 mar' : 'Sun Mar 8', time: '11:00', avail: false }].
            map((slot) =>
            <div
              key={slot.day + slot.time}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                borderRadius: '10px',
                background: slot.avail ? 'rgba(0,212,255,0.08)' : 'rgba(240,247,255,0.04)',
                border: `1px solid ${slot.avail ? 'rgba(0,212,255,0.2)' : 'rgba(240,247,255,0.06)'}`
              }}>
              
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: slot.avail ? '#F0F7FF' : 'rgba(240,247,255,0.35)' }}>{slot.day}</p>
                  <p style={{ fontSize: '12px', color: slot.avail ? '#00D4FF' : 'rgba(240,247,255,0.25)' }}>{slot.time}</p>
                </div>
                <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: '100px',
                  background: slot.avail ? 'rgba(37,211,102,0.15)' : 'rgba(255,90,90,0.12)',
                  color: slot.avail ? '#25D366' : '#ff7070'
                }}>
                
                  {slot.avail ? lang === 'it' ? 'Libero' : 'Free' : lang === 'it' ? 'Pieno' : 'Full'}
                </span>
              </div>
            )}
          </div>
          <Link href="/booking" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
            <Icon name="CalendarDaysIcon" size={16} variant="solid" />
            {lang === 'it' ? 'Controlla disponibilità' : 'Check availability'}
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: '32px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          zIndex: 2
        }}>
        
        <span style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(240,247,255,0.3)' }}>
          {lang === 'it' ? 'Scorri' : 'Scroll'}
        </span>
        <div
          style={{
            width: '1px',
            height: '40px',
            background: 'linear-gradient(to bottom, rgba(0,212,255,0.6), transparent)',
            animation: 'float 2s ease-in-out infinite'
          }} />
        
      </div>
    </section>
  );
}
